// Shared workspace header for the reconciliation family (Stripe + Authorize.Net workspaces).
// Layout:
//   overline + title (left)            [actions] [↻ sync] [account ▾]   (right)
//                                                   Last synced: <time>
//   [ Dashboard · Subscriptions · Tx ]   (optional section nav)
//   [ search + filters only ]            (optional filter bar — never actions)
//
// Account is a compact dropdown (an entity switcher, not a tab band). Sync + any extra actions are
// icon buttons by the account — filters describe the view, actions do something, so they never mix.
import React from 'react';
import { Box, Typography, Select, MenuItem, FormControl, IconButton, Tooltip, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ACCOUNT_LABEL } from './reconShared';
import ProviderLogo from './ProviderLogo';

export default function WorkspaceHeader({
  overline = 'Payments', title, provider,
  account = 'all', accounts = [], onAccount, accountAllOption = true,
  onSync, syncing = false, syncTitle = 'Sync now — pull fresh data for this account', lastSynced,
  actions,
  sectionNav,
  filterBar,
}) {
  const accountLabel = account === 'all'
    ? (accountAllOption ? 'All accounts' : null)
    : (accounts.find((a) => a.key === account)?.name || ACCOUNT_LABEL[account] || account);
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          {provider
            ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <ProviderLogo provider={provider} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}{accountLabel ? ` (${accountLabel})` : ''}</Typography>
              </Box>
            )
            : (
              <>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>{overline}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}{accountLabel ? ` (${accountLabel})` : ''}</Typography>
              </>
            )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {actions}
            {onSync && (
              <Tooltip title={syncTitle}>
                <span>
                  <IconButton size="small" onClick={onSync} disabled={syncing} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {syncing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {(accountAllOption || accounts.length > 0) && (
              <FormControl size="small">
                <Select value={account} onChange={(e) => onAccount && onAccount(e.target.value)} sx={{ minWidth: 140 }}>
                  {accountAllOption && <MenuItem value="all">All accounts</MenuItem>}
                  {accounts.map((a) => <MenuItem key={a.key} value={a.key}>{a.name || ACCOUNT_LABEL[a.key] || a.key}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          {lastSynced !== undefined && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Last synced: <b>{lastSynced || 'never'}</b></Typography>
          )}
        </Box>
      </Box>

      {(sectionNav || filterBar) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2, mb: 2 }}>
          {sectionNav ? (
            // Segmented control — an elevated pill group so the active section reads clearly instead of a
            // flat underline blended into the page.
            <Box sx={{
              display: 'inline-flex', gap: 0.5, p: 0.5, borderRadius: 2, border: 1, borderColor: 'divider',
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'grey.100'),
            }}>
              {sectionNav.items.map((it) => {
                const active = it.key === sectionNav.active;
                return (
                  <Box key={it.key} role="button" aria-pressed={active} onClick={() => sectionNav.onNav(it.key)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 2, py: 0.9, borderRadius: 1.5,
                      cursor: 'pointer', fontWeight: 600, fontSize: 14, lineHeight: 1, whiteSpace: 'nowrap', userSelect: 'none',
                      transition: 'background-color .12s, box-shadow .12s, color .12s',
                      color: active ? 'primary.main' : 'text.secondary',
                      bgcolor: active ? 'background.paper' : 'transparent',
                      boxShadow: active ? 2 : 'none',
                      '& svg': { fontSize: 18 },
                      '&:hover': active ? {} : { bgcolor: 'action.selected', color: 'text.primary' },
                    }}>
                    {it.icon}{it.label}
                  </Box>
                );
              })}
            </Box>
          ) : <span />}
          {filterBar && <Box sx={{ ml: 'auto' }}>{filterBar}</Box>}
        </Box>
      )}
    </Box>
  );
}
